// CatalogueAddItems.jsx
import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MyContext from "../../../MyContext";
import VariantManagerModal from "./VariantManagerModal";
import { useNavigate } from "react-router-dom";

// 🔹 Full-screen loader for bulk Excel upload (light + green theme)
const ScreenLoader = ({ message }) => {
  return (
    <div
      className="catalogue-add-items-loader"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1055,
        background: "rgba(255, 255, 255, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        className="spinner-border"
        role="status"
        style={{ width: "3rem", height: "3rem", borderTopColor: "#01C37D" }}
      >
        <span className="visually-hidden">Loading...</span>
      </div>
      <div
        className="mt-3 fw-semibold"
        style={{
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#016b55",
          fontSize: "0.75rem",
        }}
      >
        {message || "Processing..."}
      </div>
    </div>
  );
};

const CatalogueAddItems = ({ userID = "A1001" }) => {
  const { baseUrl, config } = useContext(MyContext);
  const navigate = useNavigate();

  const [mode, setMode] = useState("single"); // "single" | "bulk"

  // ---------- SINGLE ITEM FORM ----------
  const [form, setForm] = useState({
    itemName: "",
    status: "Y",
    itemDescription: "",
    dish: "",
    photo: "",
    ratings: "0",
    price: "",
    discount: "",
    discountPrice: "",
    barcodeNumber: "",
    expiryDate: "",
    rackNumber: "",
    categorySelect: "",
    categoryInput: "",
    subCategorySelect: "",
    subCategoryInput: "",
    variantId: "",
    variantName: "",
    iconDish: "", // Y = veg, N = non-veg, "" = none
  });
  const [savingSingle, setSavingSingle] = useState(false);

  // ---------- META (variants, subcategories, racks) ----------
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);

  const [subCategories, setSubCategories] = useState([]);
  const [rackList, setRackList] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);

  // ---------- BULK ----------
  const [bulkItems, setBulkItems] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // ============================================================
  // HELPERS
  // ============================================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Auto-calc discountPrice
    if (name === "price" || name === "discount") {
      const updated = { ...form, [name]: value };
      const priceNum = parseFloat(updated.price || "0");
      const discNum = parseFloat(updated.discount || "0");
      const discountPrice =
        priceNum > 0
          ? String(Math.max(priceNum - discNum, 0).toFixed(2))
          : "";
      setForm({ ...updated, discountPrice });
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm((prev) => ({ ...prev, photo: reader.result || "" }));
    };
    reader.readAsDataURL(file);
  };

  // 🔹 Same image upload flow you gave
  const uploadImageIfNeeded = async () => {
    if (form.photo && !form.photo.startsWith("data:")) return form.photo || "";
    if (!form.photo || !form.photo.startsWith("data:")) return "";

    const requestBody = {
      userid: userID,
      item_name: form.itemName || "Item",
      type: "IMG",
      image_data: form.photo,
    };

    const resp = await axios.post(
      "https://bumppy.in/anlook/api/v1/uploadItemImage.php",
      requestBody,
      config
    );

    if (resp?.data?.status === "0" && resp?.data?.image_url) {
      return resp.data.image_url;
    }
    throw new Error(resp?.data?.message || "Image upload failed");
  };

  const toPayloadDate = (val) => {
    if (!val) return "";
    return val.replace(/-/g, "/");
  };

  // ============================================================
  // FETCH META (variants, subcategories, racks)
  // ============================================================

  const fetchVariants = async () => {
    setLoadingVariants(true);
    try {
      const body = { userid: userID, secret: "string" };
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/getAllInventoryVariant`,
        body,
        config
      );
      if (res.data.responseCode === "00") {
        const list = (res.data.response || []).map((r) => ({
          pk_variant_id: String(r.pk_variant_id ?? r.variantId ?? ""),
          variant_title: r.variant_title ?? r.variantTitle ?? "",
          category: r.category ?? "",
        }));
        setVariants(list);
      } else {
        setVariants([]);
      }
    } catch (err) {
      console.error(err);
      setVariants([]);
    } finally {
      setLoadingVariants(false);
    }
  };

  const fetchMeta = async () => {
    setMetaLoading(true);
    try {
      // Subcategories
      const subRes = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/getInventorySubCategories`,
        { userid: userID, category: "" },
        config                                
      );
      if (subRes.data.responseCode === "00") {
        setSubCategories(subRes.data.response || []);
      } else {
        setSubCategories([]);
      }

      // Racks
      const rackRes = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/getRackDetails`,
        { userid: userID },
        config
      );
      if (rackRes.data.responseCode === "00") {
        setRackList(rackRes.data.response || []);
      } else {
        setRackList([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading categories / rack details");
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    fetchVariants();
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    subCategories.forEach((sc) => {
      if (sc.category) set.add(sc.category);
    });
    return Array.from(set);
  }, [subCategories]);

  const filteredSubcategories = useMemo(() => {
    if (form.categorySelect) {
      return subCategories.filter((sc) => sc.category === form.categorySelect);
    }
    return subCategories;
  }, [subCategories, form.categorySelect]);

  // ============================================================
  // SINGLE ITEM SUBMIT
  // ============================================================

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setSavingSingle(true);
    try {
      const finalCategory = form.categoryInput || form.categorySelect || "";
      const finalSubCategory =
        form.subCategoryInput || form.subCategorySelect || "";

      let finalCatId = "";
      if (form.subCategorySelect) {
        const matched = subCategories.find(
          (sc) =>
            sc.subCategory === form.subCategorySelect &&
            (!form.categorySelect || sc.category === form.categorySelect)
        );
        finalCatId = matched?.id ? String(matched.id) : "";
      }

      const uploadedPhotoUrl = await uploadImageIfNeeded();
      const expiryDatePayload = toPayloadDate(form.expiryDate);

      let variantId = form.variantId || "";
      let variantName = form.variantName || "";
      if (variantId && !variantName) {
        const found = variants.find(
          (v) => String(v.pk_variant_id) === String(variantId)
        );
        if (found) variantName = found.variant_title;
      }

      const itemPayload = {
        itemName: String(form.itemName || ""),
        itemId: "", // itemId not used in UI
        status: String(form.status || "Y"),
        itemDescription: String(form.itemDescription || ""),
        dish: String(form.dish || ""),
        photo: String(uploadedPhotoUrl || ""),
        itemNameTrans: "",
        itemDescriptionTrans: "",
        category: String(finalCategory || ""),
        ratings: String(form.ratings || "0"),
        price: String(form.price || ""),
        discount: String(form.discount || ""),
        discountPrice: String(form.discountPrice || ""),
        variantId: String(variantId || ""),
        variantName: String(variantName || ""),
        sizeTrans: "",
        iconDish: String(form.iconDish || ""), // Y / N / ""
        position: "",
        catId: String(finalCatId || ""),
        subCategory: String(finalSubCategory || ""),
        categoryDescription: "",
        customizations: "[]", // 🔹 always empty array as string
        barcodeNumber: String(form.barcodeNumber || ""),
        expiryDate: String(expiryDatePayload || ""),
        rackNumber: String(form.rackNumber || ""),
      };

      const body = {
        userid: String(userID),
        items: [itemPayload],
      };

      const res = await axios.post(
        `${baseUrl}/api/Order/v1.0/insertCatalogItem`,
        body,
        config
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage || "Item added successfully");
        // reset (except status)
        setForm((prev) => ({
          ...prev,
          itemName: "",
          itemDescription: "",
          dish: "",
          photo: "",
          ratings: "0",
          price: "",
          discount: "",
          discountPrice: "",
          barcodeNumber: "",
          expiryDate: "",
          rackNumber: "",
          categorySelect: "",
          categoryInput: "",
          subCategorySelect: "",
          subCategoryInput: "",
          variantId: "",
          variantName: "",
          iconDish: "",
        }));
      } else {
        toast.error(res.data.responseMessage || "Failed to add item");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Something went wrong");
    } finally {
      setSavingSingle(false);
    }
  };

  // ============================================================
  // BULK HELPERS (restricted columns)
  // ============================================================

  const handleDownloadSample = () => {
    // 🔹 Only the fields you specified:
    // item name , description , photo , category ,
    // price , discounted price , variant name ,
    // icondish y/n , rack , expiry (year/month/date)
    const headers = [
      "itemName",
      "itemDescription",
      "photo",
      "category",
      "price",
      "discountPrice",
      "variantName",
      "iconDish",
      "rackNumber",
      "expiryDate",
    ];

    const sampleRow = [
      "Margherita Pizza",
      "Classic cheese pizza",
      "https://example.com/margherita.jpg",
      "Food",
      "199",
      "179",
      "Regular",
      "Y", // Y = veg, N = non-veg
      "1",
      "2025/12/31", // YYYY/MM/DD
    ];

    const csv =
      headers.join(",") + "\n" + sampleRow.map((v) => `"${v}"`).join(",");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "catalogue_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = String(evt.target?.result || "");

      // --- 1) Robust CSV parser (handles quotes + commas) ---
      const parseCsv = (csvText) => {
        const rows = [];
        let currentField = "";
        let currentRow = [];
        let inQuotes = false;

        for (let i = 0; i < csvText.length; i++) {
          const ch = csvText[i];

          if (ch === '"') {
            // handle escaped quotes ("")
            if (inQuotes && csvText[i + 1] === '"') {
              currentField += '"';
              i++; // skip second quote
            } else {
              inQuotes = !inQuotes;
            }
          } else if (ch === "," && !inQuotes) {
            // field separator
            currentRow.push(currentField);
            currentField = "";
          } else if ((ch === "\n" || ch === "\r") && !inQuotes) {
            // end of row
            if (ch === "\r" && csvText[i + 1] === "\n") {
              i++; // handle \r\n
            }
            currentRow.push(currentField);
            // push only if row has any non-empty cell
            if (currentRow.some((cell) => cell.trim() !== "")) {
              rows.push(currentRow);
            }
            currentRow = [];
            currentField = "";
          } else {
            currentField += ch;
          }
        }

        // last row if not closed with newline
        if (currentField !== "" || currentRow.length > 0) {
          currentRow.push(currentField);
          if (currentRow.some((cell) => cell.trim() !== "")) {
            rows.push(currentRow);
          }
        }

        return rows;
      };

      const rows = parseCsv(text);
      if (!rows.length) return;

      // --- 2) Normalise headers ---
      const rawHeaders = rows[0].map((h) => h.trim());
      const headers = rawHeaders.map((h) => h.toLowerCase()); // e.g. "itemName" -> "itemname"

      const get = (obj, key) => {
        const val = obj[key];
        return val === null || val === undefined ? "" : String(val);
      };

      const dataRows = rows.slice(1);
      const items = dataRows.map((cols) => {
        // Build row object: { headerKey: cellValue }
        const row = {};
        headers.forEach((key, idx) => {
          const raw = cols[idx] ?? "";
          row[key] = raw.trim();
        });

        // Expected headers (any case is fine, no spaces):
        // itemName, itemDescription, photo, category,
        // price, discountPrice, variantName,
        // iconDish, rackNumber, expiryDate
        const itemName = get(row, "itemname");
        const itemDescription = get(row, "itemdescription");
        const photo = get(row, "photo");
        const category = get(row, "category");
        const price = get(row, "price");
        const discountPrice = get(row, "discountprice");
        const variantName = get(row, "variantname");
        const iconDish = get(row, "icondish");
        const rackNumber = get(row, "racknumber");
        let expiryDate = get(row, "expirydate");

        // Normalise expiry: allow both YYYY-MM-DD and YYYY/MM/DD
        if (expiryDate.includes("-")) {
          expiryDate = expiryDate.replace(/-/g, "/");
        }

        // Auto-calc discount as price - discountPrice (if both present)
        let discount = "";
        if (price && discountPrice) {
          const p = parseFloat(price) || 0;
          const dp = parseFloat(discountPrice) || 0;
          if (p && dp && p > dp) {
            discount = String((p - dp).toFixed(2));
          }
        }

        return {
          itemName: itemName,
          itemId: "", // not used in bulk
          status: "Y", // default active
          itemDescription: itemDescription,
          dish: "",
          photo: photo,
          itemNameTrans: "",
          itemDescriptionTrans: "",
          category: category,
          ratings: "0",
          price: price,
          discount: discount,
          discountPrice: discountPrice,
          variantId: "",
          variantName: variantName,
          sizeTrans: "",
          iconDish: iconDish, // Y / N
          position: "",
          catId: "",
          subCategory: "",
          categoryDescription: "",
          customizations: "[]", // 🔹 empty array as string
          barcodeNumber: "",
          expiryDate: expiryDate,
          rackNumber: rackNumber,
        };
      });

      setBulkItems(items);
      toast.success(`Parsed ${items.length} items from file`);
    };

    reader.readAsText(file);
  };

  const handleBulkSubmit = async () => {
    if (!bulkItems.length) {
      toast.error("No items parsed from file");
      return;
    }
    setBulkLoading(true);
    try {
      const body = {
        userid: String(userID),
        items: bulkItems,
      };
      const res = await axios.post(
        `${baseUrl}/api/Order/v1.0/insertCatalogItem`,
        body,
        config
      );
      if (res.data.responseCode === "00") {
        toast.success(
          res.data.responseMessage || "Bulk items added successfully"
        );
        setBulkItems([]);
      } else {
        toast.error(res.data.responseMessage || "Failed to upload items");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Something went wrong");
    } finally {
      setBulkLoading(false);
    }
  };

  // Dynamic heading based on tab
  const activeTitle =
    mode === "single" ? "Add Single Catalogue Item" : "Bulk Catalogue Upload";

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      <div className="d-flex justify-content-center text-center">
        <ToastContainer />
      </div>

      <div
        className="p-4 bg-white"
        style={{ fontFamily: "Nunito, sans-serif" }}
      >
        <div className="bg-white px-4">
          <div
            className="catalogue-add-items-shell"
            style={{
              borderRadius: "10px",
              border: "1px solid #E2E8F0",
              overflow: "hidden",
            }}
          >
            {/* HEADER: light, clean, green accent */}
            <div
              style={{
                background:
                  "radial-gradient(circle at top left, #01C37D, #009c63ff)",
                padding: "14px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #E2E8F0",
              }}
            >
              <div>
                <div
                  className="m-0 p-0"
                  style={{
                    fontSize: "2.05rem",
                    fontWeight: 700,
                    color: "#ffffffff",
                  }}
                >
                  {activeTitle}
                </div>
                <div
                  style={{
                    fontSize: "0.76rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    color: "#cfd7e2ff",
                    marginTop: "2px",
                  }}
                >
                  Catalogue Items · Control Panel
                </div>
              </div>

              <div
                className="d-flex align-items-center"
                style={{ columnGap: "10px" }}
              >
                {/* Tab pill switch */}
                <div
                  style={{
                    display: "flex",
                    padding: "2px",
                    borderRadius: "999px",
                    border: "1px solid #01C37D",
                    background: "#E8FFF6",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setMode("single")}
                    style={{
                      border: "none",
                      borderRadius: "999px",
                      padding: "4px 16px",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      backgroundColor:
                        mode === "single" ? "#01C37D" : "transparent",
                      color: mode === "single" ? "#FFFFFF" : "#01C37D",
                      transition: "all 0.15s ease-out",
                    }}
                  >
                    Single
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("bulk")}
                    style={{
                      border: "none",
                      borderRadius: "999px",
                      padding: "4px 16px",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      backgroundColor:
                        mode === "bulk" ? "#01C37D" : "transparent",
                      color: mode === "bulk" ? "#FFFFFF" : "#01C37D",
                      transition: "all 0.15s ease-out",
                    }}
                  >
                    Bulk
                  </button>
                </div>

                <button
                  type="button"
                  className="btn btn-sm"
                  style={{
                    borderColor: "#01C37D",
                    color: "#01C37D",
                    backgroundColor: "#FFFFFF",
                    fontWeight: 500,
                  }}
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>
              </div>
            </div>

            {/* BODY */}
            <div
              className="p-4"
              style={{ background: "#F8FAFC", minHeight: "320px" }}
            >
              {/* SINGLE MODE */}
              {mode === "single" && (
                <form className="row g-3" onSubmit={handleSingleSubmit}>
                  {metaLoading && (
                    <div className="col-12">
                      <div className="alert alert-info py-2 mb-2">
                        Loading categories, variants & racks...
                      </div>
                    </div>
                  )}

                  <div className="col-md-5">
                    <label className="form-label">Item Name</label>
                    <input
                      type="text"
                      name="itemName"
                      className="form-control"
                      value={form.itemName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Status</label>
                    <select
                      name="status"
                      className="form-select"
                      value={form.status}
                      onChange={handleInputChange}
                    >
                      <option value="Y">Active (Y)</option>
                      <option value="N">Inactive (N)</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Ratings (optional)</label>
                    <input
                      type="number"
                      name="ratings"
                      className="form-control"
                      value={form.ratings}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="col-md-12">
                    <label className="form-label">Description</label>
                    <textarea
                      name="itemDescription"
                      className="form-control"
                      rows={2}
                      value={form.itemDescription}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Dish / Tags</label>
                    <input
                      type="text"
                      name="dish"
                      className="form-control"
                      value={form.dish}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={handleImageChange}
                    />
                    <small className="text-muted">
                      Upload item image (uploaded via uploadItemImage.php)
                    </small>
                    {form.photo && form.photo.startsWith("data:") && (
                      <div className="mt-2">
                        <img
                          src={form.photo}
                          alt="preview"
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: 8,
                            objectFit: "cover",
                            border: "1px solid #E2E8F0",
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Veg / Non-veg icon */}
                  <div className="col-md-6">
                    <label className="form-label">Veg / Non-Veg Icon</label>
                    <select
                      name="iconDish"
                      className="form-select"
                      value={form.iconDish}
                      onChange={handleInputChange}
                    >
                      <option value="">None</option>
                      <option value="Y">Veg (Y)</option>
                      <option value="N">Non-Veg (N)</option>
                    </select>
                    <small className="text-muted">
                      This maps to <code>iconDish</code> (Y = Veg, N = Non-Veg).
                    </small>
                  </div>

                  {/* Category & Subcategory */}
                  <div className="col-md-6">
                    <label className="form-label">
                      Category (Select or Input)
                    </label>
                    <select
                      className="form-select mb-2"
                      value={form.categorySelect}
                      disabled={!!form.categoryInput}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          categorySelect: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select Category</option>
                      {categoryOptions.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Or type category name"
                      value={form.categoryInput}
                      disabled={!!form.categorySelect}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          categoryInput: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Subcategory (Select or Input)
                    </label>
                    <select
                      className="form-select mb-2"
                      value={form.subCategorySelect}
                      disabled={!!form.subCategoryInput}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          subCategorySelect: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select Subcategory</option>
                      {filteredSubcategories.map((sc) => (
                        <option key={sc.id} value={sc.subCategory}>
                          {sc.subCategory}{" "}
                          {sc.category ? `(${sc.category})` : ""}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Or type subcategory name"
                      value={form.subCategoryInput}
                      disabled={!!form.subCategorySelect}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          subCategoryInput: e.target.value,
                        }))
                      }
                    />
                  </div>

                  {/* Variant + Rack */}
                  <div className="col-md-6">
                    <div className="d-flex justify-content-between align-items-center">
                      <label className="form-label mb-0">Variant</label>
                      <button
                        type="button"
                        className="btn btn-link p-0 small"
                        style={{ color: "#01C37D" }}
                        onClick={() => setIsVariantModalOpen(true)}
                      >
                        Manage variants
                      </button>
                    </div>
                    {loadingVariants ? (
                      <div className="small text-muted">
                        Loading variants...
                      </div>
                    ) : (
                      <select
                        className="form-select"
                        value={form.variantId}
                        onChange={(e) => {
                          const val = e.target.value;
                          const selected = variants.find(
                            (v) => String(v.pk_variant_id) === String(val)
                          );
                          setForm((prev) => ({
                            ...prev,
                            variantId: val,
                            variantName: selected?.variant_title || "",
                          }));
                        }}
                      >
                        <option value="">Select variant</option>
                        {variants.map((v) => (
                          <option key={v.pk_variant_id} value={v.pk_variant_id}>
                            {v.variant_title}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Rack Number</label>
                    <select
                      className="form-select"
                      value={form.rackNumber}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          rackNumber: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select rack</option>
                      {rackList.map((rack) => (
                        <option key={rack.id} value={rack.rackNumber}>
                          {`Rack ${rack.rackNumber} – ${rack.category}${
                            Array.isArray(rack.tags) && rack.tags.length
                              ? ` (${rack.tags.join(", ")})`
                              : ""
                          }`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Price / Discount */}
                  <div className="col-md-4">
                    <label className="form-label">Price</label>
                    <input
                      type="number"
                      name="price"
                      className="form-control"
                      value={form.price}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Discount</label>
                    <input
                      type="number"
                      name="discount"
                      className="form-control"
                      value={form.discount}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Discounted Price</label>
                    <input
                      type="number"
                      name="discountPrice"
                      className="form-control"
                      value={form.discountPrice}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Misc */}
                  <div className="col-md-4">
                    <label className="form-label">Barcode Number</label>
                    <input
                      type="text"
                      name="barcodeNumber"
                      className="form-control"
                      value={form.barcodeNumber}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Expiry Date</label>
                    <input
                      type="date"
                      name="expiryDate"
                      className="form-control"
                      value={form.expiryDate}
                      onChange={handleInputChange}
                    />
                    <small className="text-muted">
                      Will be sent as <code>YYYY/MM/DD</code> to API.
                    </small>
                  </div>

                  <div className="col-12 text-end">
                    <button
                      type="submit"
                      className="btn"
                      style={{
                        backgroundColor: "#01C37D",
                        borderColor: "#01C37D",
                        color: "#FFFFFF",
                        minWidth: "120px",
                      }}
                      disabled={savingSingle}
                    >
                      {savingSingle ? "Saving..." : "Save Item"}
                    </button>
                  </div>
                </form>
              )}

              {/* BULK MODE */}
              {mode === "bulk" && (
                <div className="mt-1">
                  <div className="mb-3">
                    <p className="mb-1">
                      Download the sample CSV, fill it in Excel and upload
                      again. For bulk upload,{" "}
                      <strong>
                        images are not auto-uploaded – please provide ready
                        image URLs in the <code>photo</code> column.
                      </strong>
                    </p>

                    {/* 🔹 Rules / Column guide */}
                    <div
                      className="p-2 mt-2"
                      style={{
                        background: "#E8FFF6",
                        borderRadius: "8px",
                        border: "1px solid #01C37D33",
                        fontSize: "0.8rem",
                      }}
                    >
                      <div className="fw-semibold mb-1">
                        CSV Columns &amp; Rules
                      </div>
                      <ul className="mb-1 ps-3">
                        <li>
                          <code>itemName</code> – Item name (required).
                        </li>
                        <li>
                          <code>itemDescription</code> – Short description.
                        </li>
                        <li>
                          <code>photo</code> – Full image URL (e.g.
                          https://...).
                        </li>
                        <li>
                          <code>category</code> – Category name (e.g. Food,
                          Grocery).
                        </li>
                        <li>
                          <code>price</code> – MRP / base price.
                        </li>
                        <li>
                          <code>discountPrice</code> – Final selling price.{" "}
                          <br />
                          <span className="text-muted">
                            Discount amount is auto-calculated as price -
                            discountPrice.
                          </span>
                        </li>
                        <li>
                          <code>variantName</code> – e.g. Regular, Large, 1 Kg
                          (optional).
                        </li>
                        <li>
                          <code>iconDish</code> – Veg / Non-Veg flag:
                          <ul className="mb-0 ps-3">
                            <li>
                              <code>Y</code> = Veg
                            </li>
                            <li>
                              <code>N</code> = Non-Veg
                            </li>
                            <li>Leave blank if no icon.</li>
                          </ul>
                        </li>
                        <li>
                          <code>rackNumber</code> – Rack number (e.g. 1, 2A).
                        </li>
                        <li>
                          <code>expiryDate</code> – Expiry in{" "}
                          <strong>YYYY/MM/DD</strong> format
                          (e.g. 2025/12/31).
                        </li>
                      </ul>
                      <div className="text-muted">
                        Other fields (status, ratings, subCategory, etc.) are
                        auto-handled. <code>customizations</code> is always
                        sent as empty array <code>[]</code>.
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn btn-sm mt-2"
                      style={{
                        borderColor: "#01C37D",
                        color: "#01C37D",
                        backgroundColor: "#FFFFFF",
                        fontWeight: 500,
                      }}
                      onClick={handleDownloadSample}
                    >
                      Download Sample CSV
                    </button>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Upload filled CSV (Excel export)
                    </label>
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      className="form-control"
                      onChange={handleBulkFileChange}
                    />
                  </div>

                  {bulkItems.length > 0 && (
                    <div className="mb-2">
                      <small className="text-muted">
                        Parsed {bulkItems.length} items. Showing first 5 rows
                        for review.
                      </small>
                      <div
                        className="table-responsive mt-2"
                        style={{
                          maxHeight: 260,
                          overflowY: "auto",
                          borderRadius: "8px",
                          border: "1px solid #E2E8F0",
                          background: "#FFFFFF",
                        }}
                      >
                        <table className="table table-sm mb-0">
                          <thead
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                              background: "#E8FFF6",
                            }}
                          >
                            <tr>
                              <th>Item Name</th>
                              <th>Price</th>
                              <th>Discounted</th>
                              <th>Category</th>
                              <th>Icon (Y/N)</th>
                              <th>Rack</th>
                              <th>Expiry</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkItems.slice(0, 5).map((row, idx) => (
                              <tr key={idx}>
                                <td>{row.itemName}</td>
                                <td>{row.price}</td>
                                <td>{row.discountPrice}</td>
                                <td>{row.category}</td>
                                <td>{row.iconDish}</td>
                                <td>{row.rackNumber}</td>
                                <td>{row.expiryDate}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="text-end mt-3">
                    <button
                      type="button"
                      className="btn"
                      style={{
                        backgroundColor: "#01C37D",
                        borderColor: "#01C37D",
                        color: "#FFFFFF",
                        minWidth: "140px",
                      }}
                      disabled={bulkLoading || !bulkItems.length}
                      onClick={handleBulkSubmit}
                    >
                      {bulkLoading ? "Uploading..." : "Upload Items"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Variant manager (same as catalogue) */}
      <VariantManagerModal
        show={isVariantModalOpen}
        onHide={() => {
          setIsVariantModalOpen(false);
          fetchVariants(); // refresh after any change
        }}
        baseUrl={baseUrl}
        config={config}
        variants={variants}
        loadingVariants={loadingVariants}
        refreshVariants={fetchVariants}
      />

      {/* Full-screen loader while Excel is uploading */}
      {bulkLoading && (
        <ScreenLoader message="Uploading Excel data to catalogue..." />
      )}
    </>
  );
};

export default CatalogueAddItems;
